import React,{useEffect,useState} from "react";
import { InvoiceData } from "../OrderHistory";
import { currentLang } from "../../../utility/index";
import { invoiceItemKeys, invoiceKeyMap, invoiceKeyMapForPrint } from "../constants";
import { useTranslation } from "react-i18next";

// Import images directly for better bundling and caching
import logo from "../../../assets/main-logo.png";
import nwc from "../../../assets/nwc.jpg";

// Convert images to base64 for print compatibility
const convertImageToBase64 = (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL("image/png");
      resolve(dataURL);
    };
    img.onerror = reject;
    img.src = imageUrl;
  });
};
const metadata = {
  date: new Date(Date.now()).toLocaleString(),
  logoSrc: logo,
  logoSrcnwc: nwc,
  title: "Invoice Report",
};

interface InvoiceModalProps {
  data: InvoiceData | undefined;
}

const ComponentToPrintInvoice: React.FC<InvoiceModalProps> = ({ data }) => {
  const { t } = useTranslation();
  const [isArabic, setIsArabic] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [logoBase64, setLogoBase64] = useState<string>("");
  const [nwcBase64, setNwcBase64] = useState<string>("");
  useEffect(() => {
    const lang = currentLang();
    if (lang === "Arabic") setIsArabic(true);
    else setIsArabic(false);
    
    // Convert images to base64 for better print compatibility
    const loadImagesAsBase64 = async () => {
      try {
        const [logoDataUrl, nwcDataUrl] = await Promise.all([
          convertImageToBase64(logo),
          convertImageToBase64(nwc)
        ]);
        
        setLogoBase64(logoDataUrl);
        setNwcBase64(nwcDataUrl);
        setImagesLoaded(true);
        console.log("Images converted to base64 successfully");
      } catch (error) {
        console.error("Failed to convert images to base64:", error);
        // Fallback to original images
        setImagesLoaded(true);
      }
    };
    
    loadImagesAsBase64();
  }, []);
  
  if (!data) return null;

  function convertToMonth(monthString: number | null): string {
    if (!monthString) {
      return "Invalid month";
    }

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const monthIndex = month;
    if (monthIndex >= 0 && monthIndex < 12) {
      return monthNames[monthIndex];
    } else {
      return "Invalid month";
    }
  }

  const month = new Date(Date.now()).getMonth();
  const year = new Date(Date.now()).getFullYear();

  return (
<div
  id="print-content-invoice"
  className={`hidden print:block print:w-full print:max-w-full print:overflow-visible ${isArabic ? "rtl text-right" : "ltr text-left"}`}
  dir={isArabic ? "rtl" : "ltr"}
  style={{
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact'
  }}
>
  <style>
    {`
      @media print {
        img {
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
        #print-content-invoice img {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
        .print-logo {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          height: 64px !important;
          width: auto !important;
          max-width: none !important;
        }
        /* Chrome-specific fixes */
        @media screen and (-webkit-min-device-pixel-ratio: 0) {
          img {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print-logo {
            height: 64px !important;
            width: auto !important;
          }
        }
      }
    `}
  </style>

<div className="flex justify-between items-center mb-6">
        <div className="flex flex-col gap-2 text-8">
          <p>Date: {metadata?.date || "-"}</p>
    
            <div className="flex item center space-x-2">
              <img 
                src={logoBase64 || logo} 
                alt="Logo" 
                className="h-16 print:block print:!visible print-logo" 
                style={{
                  printColorAdjust: 'exact',
                  WebkitPrintColorAdjust: 'exact',
                  display: 'block',
                  height: '64px',
                  width: 'auto'
                }}
                onLoad={(e) => {
                  // Ensure image is loaded before printing
                  const img = e.target as HTMLImageElement;
                  img.style.display = 'block';
                }}
                crossOrigin="anonymous"
              />
              <img
                src={nwcBase64 || nwc}
                alt="NWC Logo"
                className="h-16 self-start print:block print:!visible print-logo"
                style={{
                  printColorAdjust: 'exact',
                  WebkitPrintColorAdjust: 'exact',
                  display: 'block',
                  height: '64px',
                  width: 'auto'
                }}
                onLoad={(e) => {
                  // Ensure image is loaded before printing
                  const img = e.target as HTMLImageElement;
                  img.style.display = 'block';
                }}
                crossOrigin="anonymous"
              />
                {data.QRCodeImage && (
            <div className="mt-1 ">
              <img
                src={data.QRCodeImage}
                alt="QR Code"
                className="w-24 h-24 sm:w-32 sm:h-32"
              />
            </div>
          )}
            </div>
       

        
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold">
            {metadata?.title || "Dynamic Report"}
          </h1>
        </div>
        <div className="max-w-[50px]">
          <p className="text-sm">
            <b>Month : </b>
            {convertToMonth(month)}
          </p>
          <p className="text-sm">
            <b>Year: </b>
            {year}
          </p>
        </div>
      </div>
      <div className="p-4 space-y-4 rounded-lg text-xs sm:text-sm">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {Object.entries(invoiceKeyMap).map(([label, dataKey]) => {
            const value = data[dataKey as keyof InvoiceData];

            return (
              <div key={label} className="flex justify-between border-b pb-2">
              <span className="font-semibold text-gray-700">{t(`orders.${label}`)}:</span>
              <span className="text-gray-600">
                {typeof value === "object"
                  ? JSON.stringify(value)
                  : value || "-"}
              </span>
            </div>
            
            );
          })}
        </div>
      </div>

      <div className="w-full mt-6 print:w-full">
      <div className="overflow-x-auto mt-10 print:overflow-visible">
  <h3 className="text-sm sm:text-base font-semibold mb-3 text-gray-800">
    {t("orders.deliveryInformation")}
  </h3>
  <table className="w-full border-collapse text-sm border border-gray-300" style={{ direction: 'ltr' }}>
    <thead>
      <tr className="bg-gray-100 border-b border-gray-300">
        <th className="text-left font-semibold text-gray-900 p-1 border-r border-gray-300 text-xs w-[25%]">
          {t("orders.deliveryID")}
        </th>
        <th className="text-left font-semibold text-gray-900 p-1 border-r border-gray-300 text-xs w-[25%]">
          {t("orders.deliveryDate")}
        </th>
        <th className="text-left font-semibold text-gray-900 p-1 border-r border-gray-300 text-xs w-[25%]">
          {t("orders.deliveryStatus")}
        </th>
        <th className="text-left font-semibold text-gray-900 p-1 text-xs w-[25%]">
          {t("orders.deliveryDocument")}
        </th>
      </tr>
    </thead>
    <tbody>
      {data.deliveryList.map((delivery, index) => (
        <tr key={index} className="border-b border-gray-300 hover:bg-gray-50">
          <td className="p-1 border-r border-gray-300 font-medium text-xs break-words">
            {delivery.sap_delivery_id || "-"}
          </td>
          <td className="p-1 border-r border-gray-300 text-xs break-words">
            {new Date(delivery.delivery_datetime).toLocaleDateString() || "-"}
          </td>
          <td className="p-1 border-r border-gray-300 text-xs break-words">
            {delivery.delivery_status || "-"}
          </td>
          <td className="p-1 text-xs break-words">
            {delivery.delivery_doc_s3_path ? (
              <a
                href={delivery.delivery_doc_s3_path}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                {t("orders.viewDocument")}
              </a>
            ) : (
              "-"
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>
        <table className="w-full border-collapse text-sm border border-gray-300" style={{ direction: 'ltr' }}>
          <thead>
            <tr className="bg-gray-100 border-b border-gray-300">
              {invoiceItemKeys.map((key) => (
                <th
                  key={key}
                  className="text-left font-semibold text-gray-900 p-1 border-r border-gray-300 text-xs w-[9%]"
                >
               {t(`orders.${key}`)} 
                </th>
              ))}
            </tr>
          </thead>
     
          <tbody>
              {data.NavInvoiceItems.results.map((item, index) => {
                // Define the correct order
                const order = [
                  "Itemno",
                  "MaterialNo",
                  "MaterialDescEn", // Move after MaterialNo
                  "MaterialDescAr", // Move after MaterialNo
                  "ActQtyDel",
                  "Uom",
                  "unitPrice",
                  "VATAmount",
                  "NetValue",
                  "GrossValue",
                  "Total"
                ];

                // Sort object entries based on the defined order
                const sortedEntries = Object.entries(item)
                  .filter(([key]) => order.includes(key)) // Only keep relevant keys
                  .sort(([keyA], [keyB]) => order.indexOf(keyA) - order.indexOf(keyB));

                return (
                  <tr key={index} className="border-b border-gray-300 hover:bg-gray-50">
                    {sortedEntries.map(([_, value], idx) => (
                      <td key={idx} className="p-1 border-r border-gray-300 text-xs break-words leading-tight">
                        {value || "-"}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          <thead>
            <tr className="bg-gray-200 border-b border-gray-300">
              <th className="text-left font-bold text-gray-900 p-1 border-r border-gray-300 text-xs w-[9%]">
                Total
              </th>
              <th className="text-left font-bold text-gray-900 p-1 border-r border-gray-300 text-xs w-[9%]"></th>
              <th className="text-left font-bold text-gray-900 p-1 border-r border-gray-300 text-xs w-[9%]"></th>
              <th className="text-left font-bold text-gray-900 p-1 border-r border-gray-300 text-xs w-[9%]"></th>
              <th className="text-left font-bold text-gray-900 p-1 border-r border-gray-300 text-xs w-[9%]"></th>
              <th className="text-left font-bold text-gray-900 p-1 border-r border-gray-300 text-xs w-[9%]"></th>
              <th className="text-left font-bold text-gray-900 p-1 border-r border-gray-300 text-xs w-[9%]"></th>
              <th className="text-left font-bold text-gray-900 p-1 border-r border-gray-300 text-xs w-[9%]">
                {" "}
                {data.VAT}
              </th>
              <th className="text-left font-bold text-gray-900 p-1 border-r border-gray-300 text-xs w-[9%]">
                {data.NetValue}
              </th>
              <th className="text-left font-bold text-gray-900 p-1 border-r border-gray-300 text-xs w-[9%]">
                {data.GrossValue}
              </th>
              <th className="text-left font-bold text-gray-900 p-1 text-xs w-[9%]">
                {data.TotalValue}
              </th>
            </tr>
          </thead>
        </table>
      </div>
    </div>
  );
};

export default ComponentToPrintInvoice;
